import React, { Component, ReactNode, ErrorInfo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ExclamationTriangleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { logError } from './error-handlers';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorId: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorId: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Generate a unique error ID for tracking
    const errorId = `ERR_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    return {
      hasError: true,
      error,
      errorId
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error with context
    const errorId = this.state.errorId;
    logError(error, {
      errorId,
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href
    });

    // Call the optional onError callback
    this.props.onError?.(error, errorInfo);
  }

  private reset = () => {
    this.setState({ hasError: false, error: null, errorId: null });
  };

  private reload = () => {
    window.location.reload();
  };

  private getErrorType(): 'auth' | 'network' | 'validation' | 'generic' {
    if (!this.state.error) return 'generic';
    
    const message = this.state.error.message.toLowerCase();
    
    if (message.includes('unauthorized') || message.includes('auth') || message.includes('token')) {
      return 'auth';
    }
    
    if (message.includes('network') || message.includes('fetch') || message.includes('connection')) {
      return 'network';
    }
    
    if (message.includes('validation') || message.includes('required') || message.includes('invalid')) {
      return 'validation';
    }
    
    return 'generic';
  }

  private getErrorMessage(): { title: string; description: string } {
    const errorType = this.getErrorType();
    
    switch (errorType) {
      case 'auth':
        return {
          title: 'Authentication Error',
          description: 'Your session has expired or you don\'t have permission to access this resource. Please log in again.'
        };
      case 'network':
        return {
          title: 'Connection Problem',
          description: 'We\'re having trouble connecting to our servers. Please check your internet connection and try again.'
        };
      case 'validation':
        return {
          title: 'Data Validation Error',
          description: 'There was a problem with the data you entered. Please check your inputs and try again.'
        };
      default:
        return {
          title: 'Something went wrong',
          description: 'An unexpected error occurred. Our team has been notified and will investigate the issue.'
        };
    }
  }

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(this.state.error!, this.reset);
      }

      const { title, description } = this.getErrorMessage();
      const errorType = this.getErrorType();

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="w-full max-w-md">
            <CardHeader className="text-center">
              <div className="mx-auto w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mb-4">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
              </div>
              <CardTitle className="text-xl font-semibold">{title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert variant={errorType === 'auth' ? 'destructive' : 'default'}>
                <AlertDescription>{description}</AlertDescription>
              </Alert>

              {this.state.errorId && (
                <div className="text-xs text-muted-foreground text-center">
                  Error ID: {this.state.errorId}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-2">
                <Button onClick={this.reset} variant="outline" className="flex-1">
                  <ArrowPathIcon className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
                <Button onClick={this.reload} className="flex-1">
                  Reload Page
                </Button>
              </div>

              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-4 p-2 bg-gray-100 rounded text-xs">
                  <summary className="cursor-pointer font-medium">
                    Debug Information
                  </summary>
                  <pre className="mt-2 whitespace-pre-wrap overflow-auto max-h-40">
                    {this.state.error.stack}
                  </pre>
                </details>
              )}
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

// Hook version for functional components
export function useErrorBoundary() {
  const [error, setError] = React.useState<Error | null>(null);

  const resetError = React.useCallback(() => {
    setError(null);
  }, []);

  const captureError = React.useCallback((error: Error) => {
    setError(error);
  }, []);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return { captureError, resetError };
}

// Higher-order component wrapper
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
}